import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { trackFormView, trackFormSubmit } from "@/lib/analytics";
import { hasPendingAiQuestion } from "@/lib/pendingAi";
import { Button } from "@/components/ui/button";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Briefcase, Trophy, Users, Landmark, HelpCircle,
  CheckCircle2, XCircle, Loader2, Sparkles, KeyRound, ChevronDown, ChevronUp, Eye, EyeOff,
} from "lucide-react";

const AFFILIATION_OPTIONS = [
  { value: "entreprise",     label: "Salarié d'une entreprise",       icon: Briefcase, placeholder: "Nom de votre entreprise" },
  { value: "club_sportif",   label: "Membre d'un club sportif",       icon: Trophy,    placeholder: "Nom du club sportif" },
  { value: "association",    label: "Adhérent d'une association",     icon: Users,     placeholder: "Nom de l'association" },
  { value: "administration", label: "Fonctionnaire / Administration", icon: Landmark,  placeholder: "Nom de l'administration" },
  { value: "autre",          label: "Autre",                          icon: HelpCircle,placeholder: "Précisez" },
];

export default function Register() {
  const [sp] = useSearchParams();
  const isPro = sp.get("role") === "professional";
  const [role, setRole] = useState(isPro ? "professional" : "particular");
  // Le code d'accès est désormais OPTIONNEL — replié par défaut pour ne pas bloquer l'inscription.
  const [hasCode, setHasCode] = useState(!!sp.get("code"));
  const [form, setForm] = useState({
    email: sp.get("email") || "", password: "", first_name: "", last_name: "", city: "", postal_code: "",
    affiliation_type: "", affiliation_name: "",
    access_code: sp.get("code") || "",
    referral_code: (sp.get("club") || sp.get("ref") || "").toLowerCase(),
    alerts_optin: true,
    business_name: "", business_category: "", business_phone: "",
  });
  const [clubName, setClubName] = useState("");
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [codeCheck, setCodeCheck] = useState({ state: "idle", info: null, error: "" });
  const [showPwd, setShowPwd] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { trackFormView("register"); }, []);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    const c = (form.referral_code || "").trim();
    if (!c) { setClubName(""); return; }
    api.get(`/clubs/by-code/${c}`).then((r) => setClubName(r.data?.name || "")).catch(() => setClubName(""));
  }, [form.referral_code]);

  // Vérification live du code (seulement si l'utilisateur a choisi d'en saisir un)
  useEffect(() => {
    if (role !== "particular" || !hasCode) { setCodeCheck({ state: "idle", info: null, error: "" }); return; }
    const code = (form.access_code || "").trim().toUpperCase();
    if (!code) { setCodeCheck({ state: "idle", info: null, error: "" }); return; }
    if (code.length < 4) { setCodeCheck({ state: "idle", info: null, error: "" }); return; }
    let cancelled = false;
    setCodeCheck((s) => ({ ...s, state: "loading" }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/auth/verify-code", { code });
        if (cancelled) return;
        setCodeCheck({ state: "ok", info: data, error: "" });
      } catch (e) {
        if (cancelled) return;
        setCodeCheck({ state: "ko", info: null, error: e?.response?.data?.detail || "Code invalide." });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.access_code, role, hasCode]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const selectedAff = AFFILIATION_OPTIONS.find((o) => o.value === form.affiliation_type);

  const submit = async (e) => {
    e.preventDefault();
    // Si l'utilisateur a coché "j'ai un code" mais n'a rien saisi → erreur explicite
    if (role === "particular" && hasCode && !form.access_code.trim()) {
      toast.error("Veuillez saisir votre code d'accès ou décochez « J'ai un code d'accès ».");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        role,
        access_code: hasCode ? (form.access_code || "").trim().toUpperCase() : "",
        referral_code: (sp.get("ref") || form.referral_code || "").trim().toUpperCase(),
      };
      const u = await register(payload);
      toast.success("Compte créé !");
      trackFormSubmit("register");
      const _next = sp.get("next");
      if (_next) { navigate(_next); return; }
      if (localStorage.getItem("macarte_draft")) { navigate("/ma-carte"); return; }
      if (u.role === "professional") navigate("/pro");
      else {
        sessionStorage.setItem("proxi_welcome_offer", "1");
        navigate(hasPendingAiQuestion() ? "/mon-assistant-ia" : "/compte");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de créer le compte.");
    }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-14">
      <h1 className="font-heading text-3xl font-extrabold text-center">Créer un compte</h1>
      <p className="mt-2 text-center text-sm text-slate-600">
        {role === "particular"
          ? "Gratuit · Sans engagement · Accédez aux bons plans locaux en 30 secondes"
          : "Rejoignez le réseau des commerces de proximité d'Eure-et-Loir"}
      </p>

      {form.referral_code && (
        <div data-testid="register-club-banner" className="mt-4 p-3 rounded-xl bg-gradient-to-br from-orange-50 to-white border border-orange-200 text-center text-sm text-orange-900">
          🎉 Invité par <strong>{clubName || "votre club"}</strong> — profitez gratuitement des bons plans locaux PROXIreduc-28
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl">
        <button onClick={()=>setRole("particular")} data-testid="register-role-particular"
                className={`py-2 rounded-lg text-sm font-semibold transition-colors ${role==="particular"?"bg-white shadow":""}`}>
          Particulier
        </button>
        <button onClick={()=>setRole("professional")} data-testid="register-role-professional"
                className={`py-2 rounded-lg text-sm font-semibold transition-colors ${role==="professional"?"bg-white shadow":""}`}>
          Professionnel
        </button>
      </div>

      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="register-form">
        <GoogleAuthButton label="S'inscrire avec Google" />
        <div className="relative py-1 text-center">
          <span className="absolute left-0 top-1/2 h-px w-full bg-slate-200" />
          <span className="relative z-10 bg-white px-3 text-xs text-slate-400">ou avec un e-mail</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom</Label><Input required value={form.first_name} onChange={set("first_name")} data-testid="register-first-name"/></div>
          <div><Label>Nom</Label><Input required value={form.last_name} onChange={set("last_name")} data-testid="register-last-name"/></div>
        </div>
        <div><Label>Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email"/></div>
        <div>
          <Label>Mot de passe (min 6)</Label>
          <div className="mt-1 relative">
            <Input type={showPwd ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")} className="pr-10" data-testid="register-password"/>
            <button type="button" onClick={()=>setShowPwd(v=>!v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    data-testid="register-password-toggle">
              {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Ville</Label><Input value={form.city} onChange={set("city")} data-testid="register-city"/></div>
          <div><Label>Code postal</Label><Input value={form.postal_code} onChange={set("postal_code")} data-testid="register-postal"/></div>
        </div>

        {role === "particular" && (
          <div className="pt-3 mt-2 border-t border-slate-100 space-y-3">
            {/* Découverte gratuite (par défaut) */}
            {!hasCode && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200" data-testid="register-discovery-block">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                  <Sparkles size={16}/> Découverte gratuite
                </div>
                <p className="mt-1 text-xs text-emerald-800/80">
                  Accédez immédiatement aux <strong>bons plans publics</strong> proposés par les commerces d'Eure-et-Loir. Aucun code requis.
                </p>
                <button type="button" onClick={()=>setHasCode(true)}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#E05638] hover:underline"
                        data-testid="register-toggle-code-on">
                  <KeyRound size={12}/> J'ai un code d'accès bénéficiaire (CSE / asso / mairie) <ChevronDown size={12}/>
                </button>
              </div>
            )}

            {/* Bloc code d'accès (déplié à la demande) */}
            {hasCode && (
              <div className="p-4 rounded-xl bg-orange-50/60 border border-orange-200 space-y-3" data-testid="register-code-block">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
                    <KeyRound size={16} className="text-[#E05638]"/> Code d'accès bénéficiaire
                  </div>
                  <button type="button" onClick={()=>{ setHasCode(false); setForm(f=>({...f, access_code: ""})); }}
                          className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                          data-testid="register-toggle-code-off">
                    Je n'en ai pas <ChevronUp size={12}/>
                  </button>
                </div>
                <p className="text-xs text-slate-600 -mt-1">
                  Fourni par votre employeur, votre club ou votre mairie. Débloque les offres réservées à votre organisme.
                </p>
                <div className="relative">
                  <Input value={form.access_code}
                         onChange={(e)=>setForm((f)=>({...f, access_code: e.target.value.toUpperCase()}))}
                         placeholder="Ex : MAIRIE-CHART-2026"
                         className="font-mono tracking-wider uppercase pr-10"
                         data-testid="register-access-code"/>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {codeCheck.state === "loading" && <Loader2 size={18} className="text-slate-400 animate-spin"/>}
                    {codeCheck.state === "ok" && <CheckCircle2 size={18} className="text-emerald-600"/>}
                    {codeCheck.state === "ko" && <XCircle size={18} className="text-rose-500"/>}
                  </div>
                </div>
                {codeCheck.state === "ok" && codeCheck.info && (
                  <div data-testid="access-code-ok"
                       className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
                    <CheckCircle2 size={16}/>
                    <span>Code valide · <strong>{codeCheck.info.affiliation_name}</strong>
                      <span className="text-emerald-600 ml-1 text-xs">({codeCheck.info.affiliation_type})</span>
                    </span>
                  </div>
                )}
                {codeCheck.state === "ko" && (
                  <div data-testid="access-code-ko"
                       className="flex items-center gap-2 p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">
                    <XCircle size={16}/><span>{codeCheck.error}</span>
                  </div>
                )}
              </div>
            )}

            <div className="text-sm font-semibold text-slate-800 pt-2">Votre rattachement (optionnel)</div>
            <div>
              <Label>Je suis</Label>
              <div className="mt-2 grid grid-cols-1 gap-2">
                {AFFILIATION_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = form.affiliation_type === opt.value;
                  return (
                    <button type="button" key={opt.value}
                            onClick={()=>setForm((f)=>({...f, affiliation_type: opt.value}))}
                            data-testid={`affiliation-${opt.value}`}
                            className={`flex items-center gap-3 p-3 rounded-xl border text-left text-sm transition-colors ${active?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white hover:border-slate-300"}`}>
                      <Icon size={18} className={active?"text-[#E05638]":"text-slate-500"}/>
                      <span className={active?"font-semibold text-slate-900":"text-slate-700"}>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {form.affiliation_type && (
              <div>
                <Label>{selectedAff?.placeholder || "Nom"}</Label>
                <Input value={form.affiliation_name} onChange={set("affiliation_name")}
                       placeholder={selectedAff?.placeholder}
                       data-testid="register-affiliation-name"/>
              </div>
            )}

            <label className="mt-3 flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white cursor-pointer hover:border-slate-300"
                   data-testid="register-alerts-optin-wrapper">
              <input type="checkbox" checked={form.alerts_optin}
                     onChange={(e)=>setForm((f)=>({...f, alerts_optin: e.target.checked}))}
                     className="mt-0.5 w-4 h-4"
                     data-testid="register-alerts-optin"/>
              <span className="text-sm">
                <span className="font-semibold text-slate-900">M'alerter par email des nouvelles offres</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Un email par offre publiée dans votre pôle. Désinscription à tout moment.
                </span>
              </span>
            </label>
          </div>
        )}

        {role === "professional" && (
          <div className="pt-2 mt-2 border-t border-slate-100 space-y-3">
            <div className="text-sm font-semibold text-slate-700">Votre entreprise</div>
            <div><Label>Nom de l'entreprise</Label><Input required value={form.business_name} onChange={set("business_name")} data-testid="register-business-name"/></div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.business_category} onValueChange={(v)=>setForm((f)=>({...f, business_category: v}))}>
                <SelectTrigger data-testid="register-business-cat"><SelectValue placeholder="Choisir"/></SelectTrigger>
                <SelectContent>{cats.map((c)=>(<SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Téléphone</Label><Input value={form.business_phone} onChange={set("business_phone")} data-testid="register-business-phone"/></div>
          </div>
        )}

        <Button type="submit" disabled={loading} className="w-full btn-shine text-white h-11" data-testid="register-submit">
          {loading ? "Création…" : "Créer mon compte"}
        </Button>
        <div className="text-center text-sm text-slate-500">
          Déjà un compte ? <Link to="/connexion" className="text-[#E05638] font-semibold">Se connecter</Link>
        </div>
      </form>
    </div>
  );
}
